import { useEffect, useState } from 'react';
import { formatDateTime } from 'helpers/dateTimeFormatter';
import { DAY_IN_MS } from 'helpers/constants';
import { CHART_PADDING_BOTTOM, CHART_PADDING_BOTTOM_WITH_NAV, CONNECTED_COLOR, CONNECTION_STATUS, DISCONNECTED_COLOR, MAX_NAVIGATOR_RANGE_MS, ROW_HEIGHT, NAVIGATOR_TOOLTIP_DURATION_MS } from '../constants';
import colors from 'theme/_colors.scss';


const buildSeries = (assets, rangeEnd, connectedLabel, disconnectedLabel) => {
    const connectedData = [];
    const disconnectedData = [];

    assets.forEach((asset, assetIndex) => {
        (asset.segments || []).forEach((segment) => {
            const start = new Date(segment.start).getTime();
            const end = segment.end ? new Date(segment.end).getTime() : rangeEnd;
            const isConnected = segment?.status === CONNECTION_STATUS.CONNECTED;
            const point = {
                x: start,
                x2: end,
                y: assetIndex,
                color: isConnected ? CONNECTED_COLOR : DISCONNECTED_COLOR,
                segmentStart: segment?.start,
                segmentEnd: segment?.end || null,
                segmentStatus: segment?.status,
            };

            if (isConnected) {
                connectedData.push(point);
            } else {
                disconnectedData.push(point);
            }
        });
    });

    return [
        {
            name: connectedLabel,
            color: CONNECTED_COLOR,
            data: connectedData,
        },
        {
            name: disconnectedLabel,
            color: DISCONNECTED_COLOR,
            data: disconnectedData,
        },
    ];
};

// Alternating row background bands matching Figma design
const buildPlotBands = (count) => Array.from({ length: count }, (_, i) => ({
    from: i - 0.5,
    to: i + 0.5,
    color: colors.white,
}));

const hasDataInRange = (points, min, max) => points.some((point) => point.x2 >= min && point.x <= max);

const NO_DATA_GROUP_NAME = 'connection-history-no-data-in-range';

const positionNoDataOverlay = (chart) => {
    if (!chart?.noDataInRangeGroup) { return; }
    const centerX = chart.plotLeft + chart.plotWidth / 2;
    const centerY = chart.plotTop + chart.plotHeight / 2;
    chart.noDataInRangeTitle.attr({ x: centerX, y: centerY - 6 });
    chart.noDataInRangeSubtitle.attr({ x: centerX, y: centerY + 14 });
};


const toggleNoDataOverlay = (chart, min, max, points, titleText, subtitleText) => {
    if (!chart) { return; }

    if (!chart.noDataInRangeGroup) {
        chart.noDataInRangeGroup = chart.renderer.g(NO_DATA_GROUP_NAME).attr({ zIndex: 6 }).add();
        chart.noDataInRangeTitle = chart.renderer.text('', 0, 0)
            .attr({ align: 'center' })
            .css({
                fontFamily: 'ABBvoice-Medium',
                fontSize: '12px',
                fontWeight: 500,
                color: colors.doveGray,
            })
            .add(chart.noDataInRangeGroup);
        chart.noDataInRangeSubtitle = chart.renderer.text('', 0, 0)
            .attr({ align: 'center' })
            .css({
                fontFamily: 'ABBvoice-Regular',
                fontSize: '12px',
                fontWeight: 400,
                color: colors.doveGray,
            })
            .add(chart.noDataInRangeGroup);
    }

    const visible = !hasDataInRange(points, min, max);
    chart.noDataInRangeTitle.attr({ text: titleText });
    chart.noDataInRangeSubtitle.attr({ text: subtitleText });
    chart.noDataInRangeGroup.attr({ visibility: visible ? 'visible' : 'hidden' });

    if (visible) {
        positionNoDataOverlay(chart);
    }
};

const showNavigatorTooltip = (chart, text, handleX) => {
    if (!chart.navigatorTooltip) {
        chart.navigatorTooltip = document.createElement('div');
        chart.navigatorTooltip.className = 'connection-history-chart__navigator-tooltip';
        chart.container.appendChild(chart.navigatorTooltip);
    }
    const el = chart.navigatorTooltip;
    el.textContent = text;
    el.style.display = 'block';
    el.style.left = `${Math.max(0, Math.min(handleX - el.offsetWidth / 2, chart.chartWidth - el.offsetWidth))}px`;
    el.style.top = `${chart.navigator.top - el.offsetHeight - 12}px`;
    clearTimeout(chart.navigatorTooltipTimer);
    chart.navigatorTooltipTimer = setTimeout(() => { el.style.display = 'none'; }, NAVIGATOR_TOOLTIP_DURATION_MS);
};

const checkNavigatorMaxRange = (chart, event, totalRangeMs, text) => {
    const nav = chart.navigator;
    if (!nav || !(nav.grabbedLeft || nav.grabbedRight)) { return; }
    const maxRangePx = MAX_NAVIGATOR_RANGE_MS / totalRangeMs * nav.size;
    if (nav.range < maxRangePx - 1) { return; }
    const mouseX = event.clientX - chart.container.getBoundingClientRect().left - nav.left;
    const handleX = nav.grabbedLeft ? nav.zoomedMin : nav.zoomedMax;
    const isExpanding = nav.grabbedLeft ? mouseX < handleX : mouseX > handleX;
    if (isExpanding) {
        showNavigatorTooltip(chart, text, nav.left + handleX);
    }
};

const useChartConfig = (assets, fromDate, toDate, translateFn) => {
    const [options, setOptions] = useState({});
    useEffect(() => {
        if (!assets || !fromDate || !toDate) { return; } const rangeStart = new Date(fromDate).getTime();
        const rangeEnd = new Date(toDate).getTime();
        const totalRangeMs = rangeEnd - rangeStart;
        const totalRangeDays = Math.round(totalRangeMs / DAY_IN_MS);
        const showNavigator = totalRangeDays >= MAX_NAVIGATOR_RANGE_MS / DAY_IN_MS;

        const viewportMin = showNavigator ? Math.max(rangeEnd - MAX_NAVIGATOR_RANGE_MS, rangeStart) : rangeStart;

        const categories = assets.map((a) => a?.assetName || '');
        const chartHeight = assets.length * ROW_HEIGHT +
            (showNavigator ? CHART_PADDING_BOTTOM_WITH_NAV : CHART_PADDING_BOTTOM);

        const connectedLabel = translateFn('ABB.Powertrain.Frontend.gatewayManagementConnectionPeriod');
        const disconnectedLabel = translateFn('ABB.Powertrain.Frontend.gatewayManagementDisconnectionPeriod');
        const presentLabel = translateFn('ABB.Powertrain.Frontend.gatewayManagementPresentLabel');
        const connectedTooltipLabel = translateFn('ABB.Powertrain.Frontend.gatewayManagementAssetConnected');
        const disconnectedTooltipLabel = translateFn('ABB.Powertrain.Frontend.gatewayManagementAssetDisconnected');
        const noDataInRangeTitle = translateFn('ABB.Powertrain.Frontend.gatewayManagementNoDataInSelectedPeriod');
        const noDataInRangeSubtitle = translateFn('ABB.Powertrain.Frontend.gatewayManagementAdjustTimeRangeAndTryAgain');
        const navigatorMaxRangeText = translateFn('ABB.Powertrain.Frontend.gatewayManagementNavigatorMaxRangeTooltip');

        const series = buildSeries(assets, rangeEnd, connectedLabel, disconnectedLabel);
        const plotBands = buildPlotBands(assets.length);
        const allPoints = [...series[0].data, ...series[1].data];

        setOptions({
            chart: {
                type: 'xrange',
                height: chartHeight,
                animation: false,
                marginTop: 10,
                events: {
                    load: function () {
                        this.xAxis[0].setExtremes(viewportMin, rangeEnd, false);
                        toggleNoDataOverlay(this, viewportMin, rangeEnd, allPoints, noDataInRangeTitle, noDataInRangeSubtitle);
                        const hide = () => this.tooltip?.hide();
                        window.addEventListener('scroll', hide, true);
                        window.addEventListener('wheel', hide, true);
                        this._hideTooltip = hide;
                        if (showNavigator) {
                            this._navigatorMove = (e) => checkNavigatorMaxRange(this, e, totalRangeMs, navigatorMaxRangeText);
                            globalThis.addEventListener('mousemove', this._navigatorMove);
                        }
                    },
                    redraw: function () {
                        positionNoDataOverlay(this);
                    },
                    destroy: function () {
                        const hide = this._hideTooltip;
                        if (hide) {
                            window.removeEventListener('scroll', hide, true);
                            window.removeEventListener('wheel', hide, true);
                        }
                        globalThis.removeEventListener('mousemove', this._navigatorMove);
                        clearTimeout(this.navigatorTooltipTimer);
                    },
                },
            },
            title: { text: '' },
            credits: { enabled: false },
            exporting: { enabled: false },
            xAxis: {
                visible: false,
                type: 'datetime', min: viewportMin,
                max: rangeEnd, maxRange: MAX_NAVIGATOR_RANGE_MS,
                lineColor: colors.alto,
                tickColor: colors.alto,
                events: {
                    afterSetExtremes: function (e) {
                        toggleNoDataOverlay(this.chart, e.min, e.max, allPoints, noDataInRangeTitle, noDataInRangeSubtitle);
                    },
                },
                labels: {
                    style: {
                        fontSize: '11px',
                        fontFamily: 'ABBvoice-Regular',
                        color: colors.mineShaft,
                    },
                },
            },
            yAxis: {
                title: { text: null },
                categories,
                reversed: true,
                min: -0.5,
                max: categories.length - 0.5,
                tickPositions: categories.map((_, i) => i),
                gridLineWidth: 0,
                lineWidth: 1,
                lineColor: colors.black,
                plotBands,
                height: categories.length * ROW_HEIGHT,
                labels: {
                    useHTML: true,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'ABBvoice-Regular',
                        color: colors.deionizerBlack,
                    },
                    formatter: function () {
                        const name = this.value || '';
                        const display = name?.length > 28 ? `${name.substring(0, 28)}\u2026` : name;
                        return `<span title="${name.replaceAll('"', '&quot;')}" style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;">${display}</span>`;
                    },
                },
            },
            legend: {
                enabled: true,
                useHTML: true,
                symbolWidth: 0,
                symbolHeight: 0,
                symbolPadding: 0,
                align: 'right',
                layout: 'horizontal',
                verticalAlign: 'bottom',
                itemStyle: {
                    fontSize: '13px',
                    fontFamily: 'ABBvoice-Regular',
                    fontWeight: 400,
                    color: colors.deionizerBlack,

                },
                labelFormatter: function () {
                    const color = this?.userOptions?.color;
                    return (
                        '<span style="display:flex;align-items:center;gap:8px;">' +
                        `<span style="width:16px;height:16px;background:${color};display:flex;align-items:center;justify-content:center;"></span>` +
                        `<span>${this.name}</span>` +
                        '</span>'
                    );
                },
            },
            tooltip: {
                useHTML: true,
                outside: true,
                backgroundColor: 'transparent',
                borderWidth: 0,
                borderRadius: 0,
                padding: 0,
                shadow: false,
                stickyTracking: false,
                hideDelay: 0,
                positioner: function (labelWidth, labelHeight, point) {
                    const chart = this.chart;
                    const xAxis = chart.xAxis[0];
                    const yAxis = chart.yAxis[0];
                    const rect = chart.container.getBoundingClientRect();
                    const scrollX = window.pageXOffset || 0;
                    const scrollY = window.pageYOffset || 0;
                    const chartPageLeft = rect.left + scrollX;
                    const chartPageTop = rect.top + scrollY;

                    // Resolve the hovered xrange point (has both x and x2).
                    let hp = chart.hoverPoint;
                    if (chart.hoverPoints?.length) {
                        hp = chart.hoverPoints.find((p) => typeof p.x2 === 'number') || hp;
                    }

                    // ── X: centre box on the visible portion of the bar ─────
                    let barCenterContainerX;
                    if (hp && typeof hp.x === 'number' && typeof hp.x2 === 'number') {
                        const visStart = Math.max(hp.x, xAxis.min);
                        const visEnd = Math.min(hp.x2, xAxis.max);
                        const midMs = visEnd > visStart ? (visStart + visEnd) / 2 : hp.x;
                        barCenterContainerX = xAxis.toPixels(midMs);
                    } else {
                        barCenterContainerX = point.plotX;
                    }
                    const barCenterPageX = chartPageLeft + barCenterContainerX;
                    const idealX = barCenterPageX - labelWidth / 2;
                    const plotLeft = chartPageLeft + chart.plotLeft;
                    const plotRight = plotLeft + chart.plotWidth;
                    const x = Math.max(plotLeft, Math.min(idealX, plotRight - labelWidth));
                    const caretEl = document.getElementById('ch-xrange-caret');
                    if (caretEl) {
                        const tooltipContainerLeft = x - chartPageLeft;
                        const caretLeft = Math.max(8, Math.min(
                            Math.round(barCenterContainerX - tooltipContainerLeft) - 8,
                            caretEl.parentElement
                                ? caretEl.parentElement.offsetWidth - 16
                                : labelWidth - 16
                        ));
                        caretEl.style.marginLeft = caretLeft + 'px';
                    }

                    // ── Y: above the bar row center ──────────────────────────
                    let barCenterContainerY;
                    if (hp && typeof hp.y === 'number') {
                        barCenterContainerY = yAxis.toPixels(hp.y);
                    } else {
                        barCenterContainerY = point.plotY;
                    }
                    const barCenterPageY = chartPageTop + barCenterContainerY;
                    const y = Math.max(0, barCenterPageY - labelHeight - 8);

                    return { x, y };
                },
                formatter: function () {
                    const point = this.point;
                    const chart = this.series.chart;
                    const viewMin = chart.xAxis[0].min;
                    const viewMax = chart.xAxis[0].max;

                    const statusLabel = point?.segmentStatus === CONNECTION_STATUS.CONNECTED
                        ? connectedTooltipLabel
                        : disconnectedTooltipLabel;
                    const segStartMs = new Date(point?.segmentStart).getTime();
                    const displayStartMs = Math.max(segStartMs, viewMin);
                    const startTime = formatDateTime(new Date(displayStartMs).toISOString());
                    let endTime;
                    if (point?.segmentEnd) {
                        const segEndMs = new Date(point?.segmentEnd).getTime();
                        const displayEndMs = Math.min(segEndMs, viewMax);
                        endTime = formatDateTime(new Date(displayEndMs).toISOString());
                    } else {
                        endTime = viewMax >= rangeEnd ? presentLabel
                            : formatDateTime(new Date(viewMax).toISOString());
                    }

                    return [
                        '<div style="display:inline-flex;flex-direction:column;align-items:flex-start;',
                        'filter:drop-shadow(0 4px 12px rgba(0,0,0,0.18))">',
                        '<div style="background:#FFFFFF;border-radius:8px;padding:8px 12px;',
                        'display:flex;flex-direction:column;gap:4px">',
                        '<span style="font-family:ABBvoice,sans-serif;font-size:14px;line-height:20px;',
                        'color:#1F1F1F;font-weight:500;white-space:nowrap;display:block">',
                        statusLabel,
                        '</span>',
                        '<span style="font-family:ABBvoice,sans-serif;font-size:12px;line-height:20px;',
                        'color:#1F1F1F;font-weight:400;white-space:nowrap;display:block">',
                        startTime, ' \u2013 ', endTime,
                        '</span>',
                        '</div>',
                        '<div id="ch-xrange-caret" style="width:0;height:0;border-left:8px solid transparent;',
                        'border-right:8px solid transparent;border-top:8px solid #FFFFFF;',
                        'margin-top:-1px;flex-shrink:0"></div>',
                        '</div>',
                    ].join('');
                },
            },
            navigator: {
                enabled: showNavigator,
                height: 40,
                maskFill: 'rgba(0, 81, 255, 0.10)',
                outlineColor: '#CCCCCC',
                outlineWidth: 1,
                handles: {
                    enabled: true,
                    backgroundColor: '#FFFFFF',
                    borderColor: 'rgba(0, 81, 255, 0.6)',
                },
                xAxis: {
                    min: rangeStart,
                    max: rangeEnd,
                    type: 'datetime',
                    tickInterval: 7 * 24 * 3600 * 1000,
                    dateTimeLabelFormats: {
                        week: '%b %e',
                        day: '%b %e',
                    },
                    tickWidth: 1,
                    tickLength: 2,
                    tickColor: colors.alto,
                    lineWidth: 1,
                    lineColor: colors.alto,
                    gridLineWidth: 1,
                    gridLineColor: colors.alto,
                    labels: {
                        style: {
                            fontSize: '10px',
                            fontFamily: 'ABBvoice-Regular',
                            color: colors.mineShaft,
                        },
                    },
                },
                series: {
                    type: 'line',
                    lineWidth: 0,
                    marker: { enabled: false },
                },
            },
            scrollbar: {
                enabled: false,
            },
            plotOptions: {
                xrange: {
                    borderRadius: 0,
                    borderWidth: 0,
                    pointWidth: 16,
                    minPointLength: 2,
                    grouping: false,
                    dataLabels: { enabled: false },
                    states: {
                        hover: { borderWidth: 2, borderColor: '#000000' },
                        inactive: { opacity: 1 },
                    },
                    events: {
                        legendItemClick: () => false,
                    },
                },
            },
            series,
        });
    }, [assets, fromDate, toDate, translateFn]);

    return options;
};

export default useChartConfig;

